import { useEffect, useRef, useState } from "react";

import { isNullOrUndefined } from "@smart/itops-utils-basic";
import {
  useLoadFields,
  useLoadFormForEdit,
  useLoadGroups,
  useLoadSections,
} from "@smart/manage-gql-client-dom";

const TIMEOUT_SEC = 600;

export type FormLoadingStatus =
  | "checking"
  | "created"
  | "timedOut"
  | "fileEncrypted"
  | undefined;

export const useLoadFormWithLoadingStatus = ({
  formUri,
  shouldCheck,
  formPollInterval,
}: {
  formUri: string;
  shouldCheck: boolean;
  formPollInterval?: number;
}) => {
  const statusRef = useRef<string | null | undefined>(undefined);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [loadingStatus, setLoadingStatus] = useState<FormLoadingStatus>();

  const getFormPollInterval = () => {
    if (!isNullOrUndefined(formPollInterval)) {
      return formPollInterval;
    }
    return loadingStatus === "checking" ? 1000 : 0;
  };
  const form = useLoadFormForEdit(
    { uri: formUri },
    {
      fetchPolicy: "cache-and-network",
      pollInterval: getFormPollInterval(),
    },
  );
  const sections = useLoadSections({ formUri });
  const groups = useLoadGroups({ formUri });
  const fields = useLoadFields({ formUri });

  useEffect(() => {
    if (!form.result) return;

    if (
      form.result.creationStatus === "failedToGenerateQuestions-fileEncrypted"
    ) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setLoadingStatus("fileEncrypted");
      statusRef.current = form.result.creationStatus;
      return;
    }

    if (!form.result.creationStatus && loadingStatus === "checking") {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      sections.refetch().catch(console.error);
      groups.refetch().catch(console.error);
      fields.refetch().catch(console.error);

      setLoadingStatus("created");
    }

    if (form.result.creationStatus && !loadingStatus && shouldCheck) {
      setLoadingStatus("checking");

      if (!timeoutRef.current)
        timeoutRef.current = setTimeout(
          () => setLoadingStatus("timedOut"),
          TIMEOUT_SEC * 1000,
        );
    }

    if (
      form.result.creationStatus &&
      !statusRef.current &&
      loadingStatus === "created"
    ) {
      setLoadingStatus(undefined);
    }
    statusRef.current = form.result.creationStatus;
  }, [form.result?.creationStatus, loadingStatus]);

  return { loadingStatus, form, sections, groups, fields };
};
